/**
 * Google Ads conversion tracking utility.
 *
 * This module provides functions for tracking Google Ads conversions,
 * specifically for "Buy Tickets" click events on venue pages.
 */

import { SentryFeatureTags, logException } from "@punchup/sentry";

// Local type for window with gtag (avoids global type pollution)
type WindowWithGtag = Window &
  typeof globalThis & {
    gtag?: (
      command: string,
      action: string,
      params?: Record<string, unknown>
    ) => void;
  };

export interface GoogleAdsConversionParams {
  showId?: string;
  venueId?: string;
  comedianIds?: string[];
  value?: number;
  currency?: string;
}

const GOOGLE_ADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID;
const CONVERSION_LABEL = process.env.NEXT_PUBLIC_GOOGLE_ADS_CONVERSION_LABEL;
const DEFAULT_VALUE = 30.0;
const DEFAULT_CURRENCY = "USD";
const MAX_GTAG_RETRIES = 3;
const GTAG_RETRY_DELAY_MS = 500;

/**
 * Track a Google Ads conversion for ticket purchases.
 * This should be called when a user clicks "Buy Tickets" on venue pages.
 *
 * @param params - Optional parameters for the conversion
 * @param retryCount - Internal retry counter (do not set manually)
 */
export const trackGoogleAdsConversion = (
  params?: GoogleAdsConversionParams,
  retryCount = 0
): void => {
  // Skip if running on server
  if (typeof window === "undefined") {
    return;
  }

  // Skip if IDs not configured
  if (!GOOGLE_ADS_ID || !CONVERSION_LABEL) {
    return;
  }

  // Retry if gtag not loaded yet (up to MAX_GTAG_RETRIES times)
  const windowWithGtag = window as WindowWithGtag;
  if (typeof windowWithGtag.gtag !== "function") {
    if (retryCount < MAX_GTAG_RETRIES) {
      setTimeout(
        () => trackGoogleAdsConversion(params, retryCount + 1),
        GTAG_RETRY_DELAY_MS
      );
    } else {
      logException(new Error("gtag not loaded after retries exhausted"), {
        tags: { feature: SentryFeatureTags.GoogleAds },
        extra: { retryCount, params },
      });
    }
    return;
  }

  // Generate a unique transaction ID for deduplication
  const transactionId = params?.showId
    ? `${params.showId}-${Date.now()}`
    : `ticket-${Date.now()}`;

  windowWithGtag.gtag("event", "conversion", {
    send_to: `${GOOGLE_ADS_ID}/${CONVERSION_LABEL}`,
    value: params?.value ?? DEFAULT_VALUE,
    currency: params?.currency ?? DEFAULT_CURRENCY,
    transaction_id: transactionId,
  });
};
